/**
 * Chat-list bucketing for the research history sidebar.
 *
 * Extracted from the legacy sidebar so the new surface reuses the same rule
 * rather than growing a second one that drifts. Buckets the LOADED WINDOW —
 * the list is paginated, so these groups describe what has been fetched so
 * far, never "all your chats".
 */

import { ensureUTCString } from '../../../utils/functions';
import { ChatGroupData } from '../../../store/actions/ChatAction';

export const CHAT_PAGE_SIZE = 20;

/** Bucket labels, in display order. Untranslated — run through `t()`. */
export const BUCKET_ORDER = ['Today', 'This Week', 'Last Week', 'Last 30 Days', 'Older'] as const;
export type BucketLabel = (typeof BUCKET_ORDER)[number];

export interface ChatBucket {
  label: BucketLabel;
  chats: ChatGroupData[];
}

/** The timestamp a chat sorts and buckets by, across both field spellings. */
export function chatTimestamp(chat: ChatGroupData): string {
  return (
    chat.LastModified || chat.last_message_at || chat.updated_at || chat.created_at || ''
  );
}

export function chatId(chat: ChatGroupData): string {
  return chat.ChatGroupID || chat.uuid || '';
}

export function chatTitle(chat: ChatGroupData): string {
  return chat.ChatTitle || chat.title || '';
}

/**
 * What a chat is called before the model has named it.
 *
 * Untranslated — run through `t()` where it is rendered.
 */
export const NEW_SESSION_TITLE = 'New Session';

/**
 * Labels that are a stand-in rather than a name.
 *
 * A chat row is created before anything has been asked, so the backend has
 * to put SOMETHING in the column; the model writes the real title when the
 * first turn completes. Everything in this set is that something — and the
 * point of the set is that none of it should ever reach the reader, because
 * "New Research" in the breadcrumb and "New Research" in the sidebar is not
 * a name, it is the absence of one, spelled out twice.
 */
const PLACEHOLDER_TITLES = new Set([
  'new research',
  'new chat',
  'new session',
  'new conversation',
  'untitled',
  'untitled chat',
  'research',
  'orchestrator (placeholder)',
]);

/** Is this label a stand-in rather than a name the model wrote? */
export function isPlaceholderTitle(raw?: string | null): boolean {
  const clean = String(raw || '').trim();
  return clean === '' || PLACEHOLDER_TITLES.has(clean.toLowerCase());
}

/**
 * The title to render, or '' when there is not one yet.
 *
 * Callers append their own placeholder — `displayChatTitle(x) || t(NEW_SESSION_TITLE)`
 * — so the untranslated constant never leaks into the DOM.
 */
export function displayChatTitle(raw?: string | null): string {
  return isPlaceholderTitle(raw) ? '' : String(raw).trim();
}

function bucketFor(timestamp: string, now: Date): BucketLabel {
  // Naive backend timestamps are UTC; parsing them as local put rows in the
  // wrong day bucket either side of midnight.
  const date = new Date(ensureUTCString(timestamp || ''));
  if (Number.isNaN(date.getTime())) return 'Older';
  const days = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Today';
  if (days <= 7) return 'This Week';
  if (days <= 14) return 'Last Week';
  if (days <= 30) return 'Last 30 Days';
  return 'Older';
}

/** Group the loaded window by recency, dropping buckets with nothing in them. */
export function bucketChats(chats: ChatGroupData[], now: Date = new Date()): ChatBucket[] {
  const byLabel = new Map<BucketLabel, ChatGroupData[]>();
  (chats || []).forEach((chat) => {
    const label = bucketFor(chatTimestamp(chat), now);
    const list = byLabel.get(label) || [];
    list.push(chat);
    byLabel.set(label, list);
  });

  return BUCKET_ORDER
    .filter((label) => (byLabel.get(label) || []).length > 0)
    .map((label) => ({ label, chats: byLabel.get(label) as ChatGroupData[] }));
}

/** Case-insensitive title filter for the search box. */
export function filterChats(chats: ChatGroupData[], query: string): ChatGroupData[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return chats || [];
  return (chats || []).filter((chat) => chatTitle(chat).toLowerCase().includes(needle));
}

/** "3 queries" — the pair count, since a turn is a query plus its answer. */
export function queryCount(chat: ChatGroupData): number | undefined {
  return chat.total_messages ? Math.ceil(chat.total_messages / 2) : undefined;
}
